"use client";

import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import styled from "styled-components";
import Flex from "../../Flex";
import { getColor } from "../../utils";
import {
  EditingContext,
  EditingCell,
  CellPositionContext,
  CellKey,
  CellRegistration,
  makeCellKey,
  parseCellKey,
} from "./EditableTableContext";
import { formatValueForCopy, coerceValueForPaste } from "./coerceValueForPaste";
import TableHeader, { TableColumn } from "../TableHeader";
import { useColumnResize } from "../useColumnResize";
import BulkCellContent, {
  BulkRowWrapper,
  BulkEditInfo,
  BULK_EDIT_BAR_HEIGHT,
} from "./BulkCellContent";
import EditableTableRow, { ROW_HANDLE_WIDTH, EditableCellWrapper } from "./EditableTableRow";

function isInputTarget(e: React.SyntheticEvent) {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  return !!target.closest('input, textarea, select, button, [role="button"], [contenteditable]');
}
const PINNED_ROW_HEIGHT = 40;
const EDITABLE_TABLE_FONT_SIZE = 13;

/** Generic column type for EditableTable — extends TableColumn with a typed renderer. */
export interface EditableTableColumn<DataType = unknown> extends TableColumn {
  renderer?: (data: DataType) => React.ReactNode;
  /** For non-EditableTableCell columns: return a string value for copy support. */
  copyValue?: (data: DataType) => string;
  /** Custom paste handler for columns that don't use EditableTableCell (e.g. images). */
  onPaste?: (rowId: string, text: string) => void;
  /** Custom clear handler for columns that don't use EditableTableCell (e.g. images). Called on Delete/Backspace. */
  onClear?: (rowId: string) => void;
  /** Prevent this column's cells from being selected (e.g. image/preview columns). */
  disableSelection?: boolean;
  /** Background color token applied to cells in this column (e.g. "grayLight"). */
  background?: string;
  /** If true, the cell in the pinned top row renders blank/white with no content. */
  pinnedRowHidden?: boolean;
}

type Props<DataType> = {
  data: DataType[];
  columns: EditableTableColumn<DataType>[];
  getRowId: (row: DataType) => string;
  rowHeight?: number;
  sort?: string;
  onSort?: (key: string) => void;
  onRowClick?: (rowId: string) => void;
  /** Optional row pinned above the column headers. Renders with the same column widths but is not part of the sortable data. */
  pinnedTopRow?: { data: DataType };
  /** Returns menu actions for a given row. If undefined or empty, no overflow button is shown. */
  getRowActions?: (rowId: string) => Array<{ title: string; onClick: () => void }> | undefined;
  /** localStorage key for persisting user-resized column widths across sessions. */
  columnWidthsStorageKey?: string;
};

const PinnedRowWrapper = styled(Flex).attrs({
  alignItems: "center",
  borderBottom: "default",
})<{ $top: number }>`
  position: sticky;
  top: ${p => p.$top}px;
  z-index: 65;
  height: ${PINNED_ROW_HEIGHT}px;
  min-height: ${PINNED_ROW_HEIGHT}px;
  flex-shrink: 0;
  background: white;
`;

const PinnedRowLabel = styled.div`
  width: ${ROW_HANDLE_WIDTH}px;
  padding-left: 8px;
  font-size: 10px;
  font-weight: 600;
  color: ${getColor("grayDark")};
  user-select: none;
`;

const EditableTable = <DataType,>({
  data,
  columns,
  getRowId,
  rowHeight = 80,
  sort,
  onSort,
  onRowClick,
  pinnedTopRow,
  getRowActions,
  columnWidthsStorageKey,
}: Props<DataType>) => {
  const [editingCell, setEditingCell] = useState<EditingCell>(null);
  const [editingInitialChar, setEditingInitialChar] = useState<string | null>(null);
  const [selectedCells, setSelectedCells] = useState<Set<CellKey>>(new Set());
  const [openRowMenuId, setOpenRowMenuId] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const cellRegistryRef = useRef<Map<CellKey, CellRegistration>>(new Map());
  // Track anchor (fixed) and focus (moving) for Shift+Arrow range selection
  const anchorCellRef = useRef<{ rowId: string; colIndex: number } | null>(null);
  const focusCellRef = useRef<{ rowId: string; colIndex: number } | null>(null);

  const { widths, getResizeHandleProps } = useColumnResize(columns, columnWidthsStorageKey);

  // --- Selection ---

  const selectCell = useCallback(
    (rowId: string, colIndex: number, metaKey: boolean, shiftKey?: boolean) => {
      if (shiftKey && anchorCellRef.current) {
        const anchor = anchorCellRef.current;
        const anchorRowIdx = data.findIndex(r => getRowId(r) === anchor.rowId);
        const focusRowIdx = data.findIndex(r => getRowId(r) === rowId);
        if (anchorRowIdx !== -1 && focusRowIdx !== -1) {
          focusCellRef.current = { rowId, colIndex };
          const minRow = Math.min(anchorRowIdx, focusRowIdx),
            maxRow = Math.max(anchorRowIdx, focusRowIdx),
            minCol = Math.min(anchor.colIndex, colIndex),
            maxCol = Math.max(anchor.colIndex, colIndex);
          const cells = new Set<CellKey>();
          for (let r = minRow; r <= maxRow; r++) {
            for (let c = minCol; c <= maxCol; c++) {
              cells.add(makeCellKey(getRowId(data[r]), c));
            }
          }
          setSelectedCells(cells);
          tableRef.current?.focus({ preventScroll: true });
          return;
        }
      }

      const key = makeCellKey(rowId, colIndex);
      if (!metaKey) {
        anchorCellRef.current = { rowId, colIndex };
        focusCellRef.current = { rowId, colIndex };
      }
      setSelectedCells(prev => {
        if (metaKey) {
          const next = new Set(prev);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        }
        return new Set([key]);
      });
      tableRef.current?.focus({ preventScroll: true });
    },
    [data, getRowId]
  );

  const clearSelection = useCallback(() => {
    setSelectedCells(prev => (prev.size === 0 ? prev : new Set()));
  }, []);

  const isCellSelected = useCallback(
    (rowId: string, colIndex: number) => selectedCells.has(makeCellKey(rowId, colIndex)),
    [selectedCells]
  );

  // --- Editing ---

  const startEdit = useCallback((rowId: string, colIndex: number, initialChar?: string) => {
    setEditingCell({ rowId, colIndex });
    setSelectedCells(new Set([makeCellKey(rowId, colIndex)]));
    setEditingInitialChar(initialChar ?? null);
    anchorCellRef.current = { rowId, colIndex };
    focusCellRef.current = { rowId, colIndex };
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditingInitialChar(null);
    tableRef.current?.focus({ preventScroll: true });
  }, []);

  // Refocus the table container when edit ends so keyboard shortcuts work
  useEffect(() => {
    if (!editingCell && tableRef.current) {
      tableRef.current.focus({ preventScroll: true });
    }
  }, [editingCell]);

  // --- Cell Registry (ref-based, no re-renders) ---

  const registerCell = useCallback((rowId: string, colIndex: number, reg: CellRegistration) => {
    cellRegistryRef.current.set(makeCellKey(rowId, colIndex), reg);
  }, []);

  const unregisterCell = useCallback((rowId: string, colIndex: number) => {
    cellRegistryRef.current.delete(makeCellKey(rowId, colIndex));
  }, []);

  const getCellRegistration = useCallback((key: CellKey) => {
    return cellRegistryRef.current.get(key);
  }, []);

  // --- Row / Column selection ---

  const selectColumn = useCallback(
    (colIndex: number) => {
      const col = columns[colIndex];
      if (col?.disableSelection) return;
      const cells = new Set<CellKey>();
      for (const row of data) {
        cells.add(makeCellKey(getRowId(row), colIndex));
      }
      const alreadySelected = data.length > 0 && [...cells].every(k => selectedCells.has(k));
      if (alreadySelected) {
        setSelectedCells(new Set());
        anchorCellRef.current = null;
        focusCellRef.current = null;
      } else {
        setSelectedCells(cells);
        if (data.length > 0) {
          anchorCellRef.current = { rowId: getRowId(data[0]), colIndex };
          focusCellRef.current = { rowId: getRowId(data[data.length - 1]), colIndex };
        }
      }
      tableRef.current?.focus({ preventScroll: true });
    },
    [columns, data, getRowId, selectedCells]
  );

  // --- Copy / Paste / Delete ---

  const getCopyText = useCallback(() => {
    if (selectedCells.size === 0) return null;
    for (const key of selectedCells) {
      const reg = cellRegistryRef.current.get(key);
      if (reg) return formatValueForCopy(reg.value, reg.type, reg.currencySymbol);
      // Fallback to column-level copyValue
      const { rowId, colIndex: colIdx } = parseCellKey(key);
      const col = columns[colIdx] as EditableTableColumn<DataType> | undefined;
      if (col?.copyValue) {
        const row =
          data.find(r => getRowId(r) === rowId) ??
          (pinnedTopRow && getRowId(pinnedTopRow.data) === rowId ? pinnedTopRow.data : undefined);
        if (row) return col.copyValue(row);
      }
    }
    return null;
  }, [selectedCells, columns, data, getRowId, pinnedTopRow]);

  // Use the native copy event (synchronous clipboardData.setData) — more reliable than
  // navigator.clipboard.writeText which requires Permissions API and can fail silently.
  const handleCopyEvent = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (editingCell && isInputTarget(e)) return;
      if (selectedCells.size === 0) return;
      const text = getCopyText();
      if (text !== null) {
        e.clipboardData.setData("text/plain", text);
        e.preventDefault();
      }
    },
    [selectedCells, getCopyText, editingCell]
  );

  const handleDelete = useCallback(() => {
    for (const key of selectedCells) {
      const { rowId, colIndex: colIdx } = parseCellKey(key);
      if (columns[colIdx]?.disableSelection) continue;
      const reg = cellRegistryRef.current.get(key);
      if (reg) {
        reg.onCommit("");
      } else {
        const col = columns[colIdx] as EditableTableColumn<DataType> | undefined;
        col?.onClear?.(rowId);
      }
    }
  }, [selectedCells, columns]);

  // Expand/contract the selection by moving the focus cell one step in the given direction,
  // then recompute selectedCells as the rectangle between anchor and new focus.
  const expandSelection = useCallback(
    (dir: "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight") => {
      const anchor = anchorCellRef.current;
      const focus = focusCellRef.current;
      if (!anchor || !focus) return;

      let newRowIdx = data.findIndex(r => getRowId(r) === focus.rowId);
      let newColIdx = focus.colIndex;

      if (dir === "ArrowUp") newRowIdx = Math.max(0, newRowIdx - 1);
      else if (dir === "ArrowDown") newRowIdx = Math.min(data.length - 1, newRowIdx + 1);
      else if (dir === "ArrowLeft") newColIdx = Math.max(0, newColIdx - 1);
      else newColIdx = Math.min(columns.length - 1, newColIdx + 1);

      const newFocusRowId = getRowId(data[newRowIdx]);
      focusCellRef.current = { rowId: newFocusRowId, colIndex: newColIdx };

      const anchorRowIdx = data.findIndex(r => getRowId(r) === anchor.rowId);
      const minRow = Math.min(anchorRowIdx, newRowIdx);
      const maxRow = Math.max(anchorRowIdx, newRowIdx);
      const minCol = Math.min(anchor.colIndex, newColIdx);
      const maxCol = Math.max(anchor.colIndex, newColIdx);

      const cells = new Set<CellKey>();
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          cells.add(makeCellKey(getRowId(data[r]), c));
        }
      }
      setSelectedCells(cells);
    },
    [data, getRowId, columns.length]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        clearSelection();
        cancelEdit();
        return;
      }

      if (isInputTarget(e)) return;

      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        e.preventDefault();
        if (e.shiftKey) {
          expandSelection(e.key);
        } else {
          const anchor = anchorCellRef.current;
          if (anchor) {
            let newRowIdx = data.findIndex(r => getRowId(r) === anchor.rowId);
            let newColIdx = anchor.colIndex;
            if (e.key === "ArrowUp") newRowIdx = Math.max(0, newRowIdx - 1);
            else if (e.key === "ArrowDown") newRowIdx = Math.min(data.length - 1, newRowIdx + 1);
            else if (e.key === "ArrowLeft") newColIdx = Math.max(0, newColIdx - 1);
            else newColIdx = Math.min(columns.length - 1, newColIdx + 1);
            const newRowId = getRowId(data[newRowIdx]);
            anchorCellRef.current = { rowId: newRowId, colIndex: newColIdx };
            focusCellRef.current = { rowId: newRowId, colIndex: newColIdx };
            setSelectedCells(new Set([makeCellKey(newRowId, newColIdx)]));
          }
        }
        return;
      }

      if (e.key === "Enter" && !editingCell) {
        const anchor = anchorCellRef.current;
        if (anchor) {
          const reg = cellRegistryRef.current.get(makeCellKey(anchor.rowId, anchor.colIndex));
          if (reg?.type === "boolean") {
            e.preventDefault();
            const boolValue = reg.value === true || reg.value === "true";
            reg.onCommit(!boolValue);
            return;
          }
          if (
            reg &&
            (reg.type === "text" ||
              reg.type === "longtext" ||
              reg.type === "number" ||
              reg.type === "percent" ||
              reg.type === "currency" ||
              reg.type === "date" ||
              reg.type === "datetime-local")
          ) {
            e.preventDefault();
            startEdit(anchor.rowId, anchor.colIndex);
            return;
          }
        }
      }

      if ((e.key === "Delete" || e.key === "Backspace") && selectedCells.size > 0) {
        e.preventDefault();
        handleDelete();
        return;
      }

      // Type-to-edit: printable key on a selected text/longtext/currency/number cell enters edit mode
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && !editingCell) {
        const anchor = anchorCellRef.current;
        if (anchor) {
          const reg = cellRegistryRef.current.get(makeCellKey(anchor.rowId, anchor.colIndex));
          if (
            reg &&
            (reg.type === "text" ||
              reg.type === "longtext" ||
              reg.type === "number" ||
              reg.type === "percent" ||
              reg.type === "currency" ||
              reg.type === "date")
          ) {
            e.preventDefault();
            startEdit(anchor.rowId, anchor.colIndex, e.key);
          }
        }
      }
    },
    [
      clearSelection,
      cancelEdit,
      expandSelection,
      handleDelete,
      selectedCells.size,
      editingCell,
      startEdit,
      data,
      getRowId,
      columns.length,
    ]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      if (editingCell && isInputTarget(e)) return;
      if (selectedCells.size === 0) return;
      e.preventDefault();

      const text = e.clipboardData.getData("text/plain").trim();
      if (!text) return;

      for (const key of selectedCells) {
        const { rowId, colIndex: colIdx } = parseCellKey(key);
        const col = columns[colIdx] as EditableTableColumn<DataType> | undefined;
        if (col?.onPaste) {
          col.onPaste(rowId, text);
          continue;
        }
        const reg = cellRegistryRef.current.get(key);
        if (!reg) continue;
        const coerced = coerceValueForPaste(text, reg.type, reg.options, reg.currencySymbol);
        if (coerced !== null) {
          reg.onCommit(coerced);
        }
      }
    },
    [selectedCells, columns, editingCell]
  );

  // --- Click outside to clear selection ---

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // If the target is no longer in the document, a React re-render removed it during
      // the same click event (e.g. a cell transitioning from display-text to an input).
      // Treat detached nodes as "inside" so we don't incorrectly clear the new selection.
      if (!document.contains(target)) return;
      if (tableRef.current && !tableRef.current.contains(target)) {
        setSelectedCells(prev => (prev.size === 0 ? prev : new Set()));
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // --- Bulk Edit ---

  const commitBulkForColumn = useCallback(
    (colIndex: number, value: string | number | boolean) => {
      for (const key of selectedCells) {
        const { colIndex: c } = parseCellKey(key);
        if (c !== colIndex) continue;
        const reg = cellRegistryRef.current.get(key);
        if (!reg) continue;
        const coerced = coerceValueForPaste(
          String(value),
          reg.type,
          reg.options,
          reg.currencySymbol
        );
        if (coerced !== null) reg.onCommit(coerced);
        else if (typeof value === "boolean") reg.onCommit(value);
      }
      tableRef.current?.focus({ preventScroll: true });
    },
    [selectedCells]
  );

  // Show bulk edit cells for each column where 2+ cells are selected
  const bulkEditInfos = useMemo((): BulkEditInfo[] => {
    if (selectedCells.size < 2) return [];
    const byCol = new Map<number, CellKey[]>();
    for (const key of selectedCells) {
      const { colIndex: c } = parseCellKey(key);
      if (!byCol.has(c)) byCol.set(c, []);
      byCol.get(c)!.push(key);
    }
    const result: BulkEditInfo[] = [];
    for (const [colIndex, keys] of byCol) {
      if (keys.length < 2) continue;
      const col = columns[colIndex] as EditableTableColumn<DataType> | undefined;
      if (!col || col.disableSelection) continue;
      const reg = cellRegistryRef.current.get(keys[0]);
      if (!reg) continue;
      const offsetLeft =
        widths.slice(0, colIndex).reduce((acc, w) => acc + w, 0) + ROW_HANDLE_WIDTH;
      const initialChecked =
        reg.type === "boolean"
          ? keys.some(k => {
              const v = cellRegistryRef.current.get(k)?.value;
              return v === true || v === "true";
            })
          : false;
      result.push({
        colIndex,
        type: reg.type,
        options: reg.options,
        offsetLeft,
        width: widths[colIndex],
        initialChecked,
        currencySymbol: reg.currencySymbol,
      });
    }
    result.sort((a, b) => a.colIndex - b.colIndex);
    return result;
  }, [selectedCells, columns, widths]);

  // --- Context ---

  const contextValue = useMemo(
    () => ({
      editingCell,
      editingInitialChar,
      startEdit,
      cancelEdit,
      selectedCells,
      selectCell,
      clearSelection,
      isCellSelected,
      registerCell,
      unregisterCell,
      getCellRegistration,
    }),
    [
      editingCell,
      editingInitialChar,
      startEdit,
      cancelEdit,
      selectedCells,
      selectCell,
      clearSelection,
      isCellSelected,
      registerCell,
      unregisterCell,
      getCellRegistration,
    ]
  );

  const totalWidth = widths.reduce((acc, w) => acc + w, 0) + ROW_HANDLE_WIDTH;

  const bulkBarHeight = bulkEditInfos.length > 0 ? BULK_EDIT_BAR_HEIGHT : 0;
  const pinnedRowTop = bulkBarHeight;
  const headerTop = bulkBarHeight + (pinnedTopRow ? PINNED_ROW_HEIGHT : 0);

  return (
    <EditingContext.Provider value={contextValue}>
      <div
        ref={tableRef}
        style={{ minWidth: totalWidth, outline: "none", fontSize: EDITABLE_TABLE_FONT_SIZE }}
        tabIndex={0}
        role="grid"
        onKeyDown={handleKeyDown}
        onCopy={handleCopyEvent}
        onPaste={handlePaste}>
        {bulkEditInfos.length > 0 && (
          <BulkRowWrapper>
            {bulkEditInfos.map(info => (
              <BulkCellContent
                key={info.colIndex}
                info={info}
                onCommit={v => commitBulkForColumn(info.colIndex, v)}
              />
            ))}
          </BulkRowWrapper>
        )}
        {pinnedTopRow &&
          (() => {
            const pinnedRowId = getRowId(pinnedTopRow.data);
            return (
              <PinnedRowWrapper $top={pinnedRowTop}>
                <PinnedRowLabel>Defaults</PinnedRowLabel>
                {columns.map((col, colIdx) => (
                  <CellPositionContext.Provider
                    key={colIdx}
                    value={{ rowId: pinnedRowId, colIndex: colIdx }}>
                    <EditableCellWrapper
                      $selected={!col.disableSelection && isCellSelected(pinnedRowId, colIdx)}
                      $background={col.pinnedRowHidden ? undefined : col.background}
                      style={{ width: widths[colIdx] }}
                      onClick={
                        col.disableSelection
                          ? () => clearSelection()
                          : (e: React.MouseEvent) => {
                              if (
                                editingCell?.rowId === pinnedRowId &&
                                editingCell?.colIndex === colIdx
                              )
                                return;
                              selectCell(pinnedRowId, colIdx, e.metaKey || e.ctrlKey, e.shiftKey);
                            }
                      }>
                      {col.pinnedRowHidden ? null : col.renderer?.(pinnedTopRow.data)}
                    </EditableCellWrapper>
                  </CellPositionContext.Provider>
                ))}
              </PinnedRowWrapper>
            );
          })()}
        <TableHeader
          columns={columns}
          widths={widths}
          sort={sort}
          onSort={onSort}
          headerSpacer={ROW_HANDLE_WIDTH}
          getResizeHandleProps={getResizeHandleProps}
          onSelectColumn={selectColumn}
          top={`${headerTop}px`}
        />
        {data.map(row => {
          const rowId = getRowId(row);
          return (
            <EditableTableRow
              key={rowId}
              row={row}
              rowId={rowId}
              columns={columns}
              widths={widths}
              rowHeight={rowHeight}
              onRowClick={onRowClick}
              getRowActions={getRowActions}
              isMenuOpen={openRowMenuId === rowId}
              setOpenRowMenuId={setOpenRowMenuId}
            />
          );
        })}
      </div>
    </EditingContext.Provider>
  );
};

export default EditableTable;
